import type { FC, RefObject } from "react";
import { useCallback, useState } from "react";

import styledComponents, { css } from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import type { IDropDownItem } from "@similarweb/ui-components";
import { EllipsisDropdownItem, Popover, PopoverContent, PopoverTrigger, ScrollArea } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { i18nFilter } from "app/filters/i18nFilter";
import { TableSelectionKeywordsWarning } from "pro-features/components/TableSelection/src/TableSelectionKeywordsWarning";
import { TableSelectionNewGroup } from "pro-features/components/TableSelection/src/TableSelectionNewGroup";

import { ESegmentGroupMode, useSegmentGroupSelectionController } from "./useSegmentGroupSelectionController";

const SCROLL_AREA_HEIGHT = 200;

const DropDownItem = styledComponents(EllipsisDropdownItem)<IDropDownItem>`
    ${css`
        svg {
            width: 100%;
            height: 100%;
        }
    `}
`;

const StyledPopoverContent = styledComponents(PopoverContent)`
    padding: 0;
`;

const ItemStyled = styledComponents(EllipsisDropdownItem)<
    IDropDownItem & { $appendSeparator?: boolean; $disableHover?: boolean }
>`
    padding: 0 18px;
    margin-bottom: 1px;
    position: relative;
    ${({ $appendSeparator }) =>
        $appendSeparator &&
        css`
            &:after {
                content: "";
                width: calc(100% - 16px);
                height: 1px;
                background-color: ${colorsPalettes.carbon[100]};
                position: absolute;
                bottom: 0;
                left: 50%;
                transform: translateX(-50%);
            }
        `}
    ${({ $disableHover }) =>
        $disableHover &&
        css`
            &:hover {
                background-color: inherit;
                cursor: default;
            }
        `}
    svg {
        width: 100%;
        height: 100%;
    }
`;

interface ISegmentGroupSelectionPopoverProps {
    triggerRef: RefObject<HTMLButtonElement>;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedSegmentIds: string[];
    clearAllSelectedRows: () => void;
    showToast: (href: string | null, text: string, label: string | null) => void;
}

export const SegmentGroupSelectionPopover: FC<ISegmentGroupSelectionPopoverProps> = ({
    triggerRef,
    open,
    onOpenChange,
    selectedSegmentIds,
    clearAllSelectedRows,
    showToast,
}) => {
    const i18n = i18nFilter();
    const [groupName, setGroupName] = useState("");
    const resetGroupName = useCallback(() => {
        setGroupName("");
    }, []);

    const handleToast = useCallback(
        (href: string | null, text: string, label: string | null) => {
            resetGroupName();
            onOpenChange(false);
            showToast(href, text, label);
        },
        [onOpenChange, resetGroupName, showToast],
    );

    const controller = useSegmentGroupSelectionController({
        selectedSegmentIds,
        clearAllSelectedRows,
        showToast: handleToast,
    });

    const handleCancel = useCallback(() => {
        resetGroupName();
        controller.handleCancel();
    }, [controller, resetGroupName]);

    const handleSubmit = useCallback(() => {
        void controller.handleCreateGroup(groupName);
    }, [controller, groupName]);

    const handleGroupClick = useCallback(
        async (item: { id?: string; text?: string }) => {
            await controller.handleGroupClick(item);
        },
        [controller],
    );

    const handleItemClick = useCallback(
        (item: { id: string; text: string }) => () => {
            void handleGroupClick(item);
        },
        [handleGroupClick],
    );

    const handlePopoverClose = useCallback(() => {
        resetGroupName();
        controller.handleCancel();
        onOpenChange(false);
    }, [controller, onOpenChange, resetGroupName]);

    const handleCreateNewGroup = useCallback(() => {
        void handleGroupClick({});
    }, [handleGroupClick]);

    const renderFirstItem = () => {
        switch (controller.mode) {
            case ESegmentGroupMode.NEW_GROUP:
                return (
                    <ItemStyled
                        $disableHover
                        id="add-new"
                        key="item-new"
                        iconName="add"
                        $appendSeparator
                        onClick={handleCreateNewGroup}
                    >
                        {i18n("table.selection.newgroup.segment.create")}
                    </ItemStyled>
                );
            case ESegmentGroupMode.ALL_ITEMS_EXISTS:
                return (
                    <ItemStyled
                        $disableHover
                        $appendSeparator
                        key="item-group"
                        iconName="segment-folder"
                        iconSize="sm"
                    >
                        {controller.currentGroupName}
                    </ItemStyled>
                );
            case ESegmentGroupMode.MAX_ITEMS:
                return (
                    <ItemStyled
                        $disableHover
                        $appendSeparator
                        key="item-group"
                        iconName="segment-folder"
                        iconSize="sm"
                    >
                        {i18n("table.selection.newgroup.segment.create")}
                    </ItemStyled>
                );
            case ESegmentGroupMode.GROUP_LIST:
                return (
                    <ItemStyled
                        id="add-new"
                        key="item-new"
                        iconName="add"
                        $appendSeparator={controller.groups.length > 0}
                        onClick={handleCreateNewGroup}
                    >
                        {i18n("table.selection.newgroup.segment.create")}
                    </ItemStyled>
                );
        }
    };

    const renderContent = () => {
        switch (controller.mode) {
            case ESegmentGroupMode.NEW_GROUP:
                return (
                    <TableSelectionNewGroup
                        key="new-group"
                        title={i18n("table.selection.newgroup.segment.title")}
                        placeholder={i18n("table.selection.newgroup.keywords.placeholder")}
                        onCancel={handleCancel}
                        onSubmit={handleSubmit}
                        submitButtonDisabled={false}
                        error={controller.error}
                        errorMessage={controller.errorMessage}
                        groupName={groupName}
                        onGroupNameChange={setGroupName}
                        isLoading={controller.isLoading}
                        groupType="segment"
                    />
                );
            case ESegmentGroupMode.ALL_ITEMS_EXISTS:
                return (
                    <TableSelectionKeywordsWarning
                        key="all-items-exist"
                        onCancel={handleCancel}
                        message={i18n("table.selection.websites.exists")}
                    />
                );
            case ESegmentGroupMode.MAX_ITEMS:
                return (
                    <TableSelectionKeywordsWarning
                        key="max-items"
                        onCancel={handleCancel}
                        message={i18n("table.selection.websites.max.websites", { count: 0 })}
                    />
                );
            case ESegmentGroupMode.GROUP_LIST:
                return controller.groups.map((item, index) => (
                    <DropDownItem
                        key={`item-${index}`}
                        id={item.id}
                        iconName="segment-folder"
                        iconSize="sm"
                        onClick={handleItemClick(item)}
                    >
                        {item.text}
                    </DropDownItem>
                ));
        }
    };

    const content = renderContent();
    const isGroupList = controller.mode === ESegmentGroupMode.GROUP_LIST;

    return (
        <Popover
            open={open}
            placement="bottom-start"
            isClickOutsideEnabled
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    handlePopoverClose();
                }
            }}
        >
            <PopoverTrigger elementRef={triggerRef} />
            <StyledPopoverContent>
                <Box
                    width="320px"
                    data-automation="segments-comparison-popover"
                >
                    {renderFirstItem()}
                    {isGroupList ? (
                        <ScrollArea
                            style={{ maxHeight: SCROLL_AREA_HEIGHT, minHeight: 0 }}
                            verticalScrollbarStyle={{ borderRadius: 5 }}
                            horizontal={false}
                            smoothScrolling
                            minScrollSize={48}
                        >
                            {content}
                        </ScrollArea>
                    ) : (
                        content
                    )}
                </Box>
            </StyledPopoverContent>
        </Popover>
    );
};
